import { useState, useEffect } from 'react';
import { Users, X } from 'lucide-react';
import apiService from '../../services/api';

interface User {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  username?: string | null;
}

interface JobUserAssignerProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  error?: string;
}

function displayName(u: User): string {
  const parts = [u.first_name, u.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : u.username || u.email || `User #${u.id}`;
}

function JobUserAssigner({ selectedIds, onChange, error }: JobUserAssignerProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiService.get('/users')
      .then((res: any) => {
        // apiService unwraps response.data — res = { success, data }
        const list = res.data?.users || res.users || res.data || [];
        if (Array.isArray(list)) setUsers(list);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u => {
    if (selectedIds.includes(u.id)) return false;
    if (!search) return true;
    const lower = search.toLowerCase();
    return (u.first_name || '').toLowerCase().includes(lower) ||
           (u.last_name || '').toLowerCase().includes(lower) ||
           (u.username || '').toLowerCase().includes(lower) ||
           (u.email || '').toLowerCase().includes(lower);
  });

  const selectedUsers = users.filter(u => selectedIds.includes(u.id));

  const add = (id: number) => {
    onChange([...selectedIds, id]);
    setSearch('');
  };

  const remove = (id: number) => {
    onChange(selectedIds.filter(i => i !== id));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        <Users className="h-4 w-4 inline mr-1" />
        Assign To
      </label>

      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedUsers.map(u => (
            <span key={u.id} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-sm px-2.5 py-1 rounded-full">
              {displayName(u)}
              <button type="button" onClick={() => remove(u.id)} className="hover:text-red-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={loading ? 'Loading users...' : 'Search users...'}
        disabled={loading}
        className={`w-full border rounded-lg px-3 py-2 text-sm ${error ? 'border-red-400' : 'border-neutral-300'} focus:ring-2 focus:ring-indigo-200`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {/* Dropdown */}
      {search.length > 0 && filtered.length > 0 && (
        <div className="border border-neutral-200 rounded-lg shadow-lg mt-1 bg-white max-h-40 overflow-y-auto">
          {filtered.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => add(u.id)}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
            >
              <span className="font-medium">{displayName(u)}</span>
              {u.email && <span className="text-neutral-400 ml-2">{u.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default JobUserAssigner;
